import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardCheck, Check, X, Gavel } from "lucide-react";
import { toast } from "sonner";
import { API, fmtErr, money } from "../lib/api";
import { useLang } from "../lib/i18n";
import { LetterDialog } from "./PrintDialog";

const CHECKS = ["valuation", "photos", "notice_sent", "notice_period_passed", "dntt_papers", "in_warehouse"];

export function AuctionPrep({ managerUp, onChanged }) {
  const { t } = useLang();
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [letter, setLetter] = useState(null);

  const load = () => API.get("/shop/auction-prep").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const sendAuction = async (r) => {
    if (!window.confirm(`${t("prep.send_auction")} ${r.loan_number}?`)) return;
    try {
      await API.post(`/shop/loans/${r.id}/auction`);
      toast.success(t("msg.sent_auction"));
      load();
      onChanged && onChanged();
    } catch (err) { toast.error(fmtErr(err)); }
  };
  const sendNotice = async (r) => {
    try {
      const { data } = await API.get(`/shop/loans/${r.id}/notice`, { params: { kind: "sale_notice" } });
      setLetter(data);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  if (!rows) return null;
  return (
    <section data-testid="auction-prep-section">
      <h2 className="font-heading text-lg font-semibold text-slate-800 mb-1 flex items-center gap-2">
        <ClipboardCheck className="h-5 w-5 text-orange-600" />{t("prep.title")} ({rows.length})
      </h2>
      <p className="text-sm text-slate-500 mb-4">{t("prep.desc")}</p>
      {rows.length === 0 ? (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 text-center text-slate-500" data-testid="auction-prep-empty">{t("prep.empty")}</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className={`bg-white rounded-xl border p-4 ${r.ready ? "border-emerald-300" : "border-slate-200"}`} data-testid={`prep-row-${r.loan_number}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono font-bold">{r.loan_number}</span>
                <span className="text-sm text-slate-600">{r.customer_name}</span>
                <span className="text-xs text-slate-500">{r.days_overdue} {t("alerts.days_overdue")} · {r.item_use === "professional" ? "PRO" : "PERS"}</span>
                <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${r.ready ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`} data-testid={`prep-ready-${r.loan_number}`}>
                  {r.ready ? t("prep.ready") : t("prep.not_ready")}
                </span>
                <div className="flex-1" />
                <span className="text-xs text-slate-500">{t("items.est_value")}: <span className="font-mono font-semibold">{money(r.estimated_value)}</span> · {t("loans.total_due")}: <span className="font-mono font-semibold text-emerald-700">{money(r.total_due)}</span></span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mt-3">
                {CHECKS.map((k) => (
                  <div key={k} className={`flex items-center gap-1.5 text-xs rounded-lg px-2 py-1.5 ${r.checks[k] ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-700"}`} data-testid={`prep-check-${r.loan_number}-${k}`}>
                    {r.checks[k] ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}{t(`prep.${k}`)}
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="text-slate-500">{r.items.map((i) => `${i.name} (${i.category})`).join(", ")}</span>
                <div className="flex-1" />
                <button data-testid={`prep-open-${r.loan_number}`} onClick={() => navigate(`/app/loans?open=${r.id}`)} className="px-3 py-1.5 rounded-lg border border-slate-300 hover:bg-slate-50">{t("prep.open_loan")}</button>
                {managerUp && !r.checks.notice_sent && (
                  <button data-testid={`prep-notice-${r.loan_number}`} onClick={() => sendNotice(r)} className="px-3 py-1.5 rounded-lg border border-orange-300 text-orange-700 hover:bg-orange-50">{t("ntc.sale_notice")}</button>
                )}
                {managerUp && (
                  <button data-testid={`prep-auction-${r.loan_number}`} onClick={() => sendAuction(r)} disabled={!r.ready}
                    className="px-3 py-1.5 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-40 flex items-center gap-1.5">
                    <Gavel className="h-3.5 w-3.5" />{t("prep.send_auction")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <LetterDialog doc={letter} title={t("ntc.sale_notice")} onClose={() => setLetter(null)} testid="prep-notice" onShared={load} />
    </section>
  );
}
